import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { makeRemoteUpdateLocationUser } from 'main/factories/usecases/update-location-user';
import { FIVE_SECONDS, showToast, TEN_SECONDS, TOAST_ERROR, TOAST_SUCCESS } from 'lib/utils';
import { UpdateLocationUser } from 'domain/usecases/update-location-user';

/**
 * Custom hook for updating a location user
 * @param {UseMutationOptions} options - Optional configuration options
 * @returns {Object} Mutation object with various properties and methods
 */
export const useUpdateLocationUser = (options?: UseMutationOptions<any, Error, UpdateLocationUser.Params, unknown>) => {
  const queryClient = useQueryClient();
  const updateLocationUserService = makeRemoteUpdateLocationUser();

  // Function to update the location user via API
  const updateLocationUserFn = async (userData: UpdateLocationUser.Params) => {
    return updateLocationUserService.updateLocationUser(userData);
  };

  const { onSuccess: customOnSuccess, ...rest } = options || {};

  return useMutation({
    mutationFn: updateLocationUserFn,

    onError: (error, variables, _context) => {
      showToast(error.message || 'Error updating user', TOAST_ERROR, TEN_SECONDS);
    },

    // When the mutation succeeds, invalidate related queries
    onSuccess: async (_data, variables, _context) => {
      // Invalidate location users queries
      await queryClient.invalidateQueries({
        queryKey: ['locationUsers', variables.locationId],
      });

      // Also invalidate general locationUsers query
      await queryClient.invalidateQueries({
        queryKey: ['locationUsers'],
      });

      showToast('User updated successfully', TOAST_SUCCESS, FIVE_SECONDS);
    },

    // Forward other options
    ...rest,
  });
};
